import { Input, Select } from '@sjuni/ui';
import { Clock, Eye, Send, UserRound } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { SubmitButton } from '@/components/SubmitButton';
import { useAuth } from '@/lib/auth/useAuth';

/**
 * docs/05-ADMIN-DASHBOARD.md §4.4 — the sidebar on every content editor.
 *
 * The part that is not decoration is the last line of it:
 *
 *   "Faculty Admins see 'Submit for review' in place of 'Publish'."
 *
 * §3 spells out why: they hold `news.update` but not `news.publish`, and
 * "Setting status to `published` is replaced in their UI by **'Submit for
 * review'**, which sets `status = draft` + a `pending_review` flag and notifies
 * Content Editors." So this component asks the *permission*, not the role — a
 * Vocational Admin holds `vocational.publish` and a Faculty Admin does not,
 * and both use this same sidebar.
 *
 * The server refuses either way. Hiding the control means an editor is never
 * offered an action that will be rejected; it is not what makes the rejection
 * happen.
 */
export type PublishStatus = 'draft' | 'scheduled' | 'published' | 'archived';

export interface PublishingSidebarProps {
  /** The `{resource}` half of §3's permission names — `news`, `vocational`. */
  resource: string;
  status: PublishStatus;
  onStatusChange: (status: PublishStatus) => void;
  publishedAt: string | null;
  onPublishedAtChange: (value: string | null) => void;
  /** §4.4 — "Last edited by / at". */
  updatedAt?: string | null;
  updatedBy?: { id: number; name: string } | null;
  /** §4.4's preview button. Absent for records with no public address yet. */
  previewUrl?: string | null;
  pendingReview?: boolean;
  saving?: boolean;
  disabled?: boolean;
}

export function PublishingSidebar({
  resource,
  status,
  onStatusChange,
  publishedAt,
  onPublishedAtChange,
  updatedAt,
  updatedBy,
  previewUrl,
  pendingReview = false,
  saving = false,
  disabled,
}: PublishingSidebarProps) {
  const { t } = useTranslation();
  const { can } = useAuth();

  const mayPublish = can(`${resource}.publish`);

  const statuses: PublishStatus[] = mayPublish
    ? ['draft', 'scheduled', 'published', 'archived']
    : // Without the permission the other three are not choices — offering them
      // and failing the save is worse than not offering them.
      ['draft'];

  return (
    <aside className="space-y-4 rounded-card border border-subtle bg-surface p-4">
      <h2 className="text-caption font-extrabold tracking-wide text-muted">
        {t('editor.publishing.title')}
      </h2>

      {mayPublish ? (
        <>
          <Select
            label={t('editor.publishing.status')}
            value={status}
            disabled={disabled}
            onChange={(event) => onStatusChange(event.target.value as PublishStatus)}
            options={statuses.map((value) => ({
              value,
              label: t(`editor.publishing.statuses.${value}`),
            }))}
          />

          {/*
            Only for `scheduled`. A date on a draft is noise, and one on a
            published record is the date it went live rather than something to
            set — §4.4 pairs the picker with the status for that reason.
          */}
          {status === 'scheduled' ? (
            <Input
              type="datetime-local"
              label={t('editor.publishing.publishAt')}
              hint={t('editor.publishing.publishAtHint')}
              name="published_at"
              disabled={disabled}
              value={toLocalInput(publishedAt)}
              onChange={(event) => onPublishedAtChange(fromLocalInput(event.target.value))}
            />
          ) : null}
        </>
      ) : (
        <p className="rounded-card bg-surface-alt p-3 text-caption text-secondary">
          {pendingReview
            ? t('editor.publishing.awaitingReview')
            : t('editor.publishing.reviewExplainer')}
        </p>
      )}

      <div className="flex flex-wrap gap-2">
        <SubmitButton loading={saving} disabled={disabled} size="sm">
          {mayPublish ? t('editor.publishing.save') : t('editor.publishing.submitForReview')}
        </SubmitButton>

        {previewUrl ? (
          <a
            href={previewUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 rounded-full border border-subtle px-4 py-2 text-body-sm font-semibold text-secondary hover:text-primary focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-navy-600"
          >
            <Eye size={16} strokeWidth={1.75} aria-hidden="true" />
            {t('editor.publishing.preview')}
          </a>
        ) : null}
      </div>

      {mayPublish ? null : (
        <p className="flex items-start gap-2 text-caption text-muted">
          <Send size={14} strokeWidth={1.75} aria-hidden="true" className="mt-0.5 shrink-0" />
          {t('editor.publishing.submitExplainer')}
        </p>
      )}

      {updatedAt ? (
        <dl className="space-y-1 border-t border-subtle pt-3 text-caption text-muted">
          <div className="flex items-center gap-1.5">
            <Clock size={13} strokeWidth={1.75} aria-hidden="true" />
            <dt className="sr-only">{t('editor.publishing.lastEdited')}</dt>
            <dd>
              <time dateTime={updatedAt}>{new Date(updatedAt).toLocaleString()}</time>
            </dd>
          </div>

          {updatedBy ? (
            <div className="flex items-center gap-1.5">
              <UserRound size={13} strokeWidth={1.75} aria-hidden="true" />
              <dt className="sr-only">{t('editor.publishing.lastEditedBy')}</dt>
              <dd>{updatedBy.name}</dd>
            </div>
          ) : null}
        </dl>
      ) : null}
    </aside>
  );
}

/**
 * `datetime-local` wants `YYYY-MM-DDTHH:mm` in *local* time; the API sends and
 * expects ISO-8601 with an offset.
 *
 * Converting through the string rather than `toISOString()` on the way in,
 * because that returns UTC and the picker would show an editor in Tbilisi a
 * time four hours off the one they chose.
 */
function toLocalInput(iso: string | null): string {
  if (!iso) {
    return '';
  }

  const date = new Date(iso);

  if (Number.isNaN(date.getTime())) {
    return '';
  }

  const pad = (value: number) => String(value).padStart(2, '0');

  return (
    `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}` +
    `T${pad(date.getHours())}:${pad(date.getMinutes())}`
  );
}

function fromLocalInput(value: string): string | null {
  if (value === '') {
    return null;
  }

  const date = new Date(value);

  return Number.isNaN(date.getTime()) ? null : date.toISOString();
}
